import { useQueryClient } from "@tanstack/react-query"
import { Link, useRouter } from "@tanstack/react-router"
import {
  ArrowDown2,
  ArrowRight2,
  ArrowUp2,
  EmojiHappy,
  Gift,
  MagicStar,
  SearchNormal1,
  Star,
  User
} from "iconsax-reactjs"
import { parseAsString, useQueryState } from "nuqs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { PiFire } from "react-icons/pi"
import InfiniteScroll from "react-infinite-scroll-component"
import PullToRefresh from "react-simple-pull-to-refresh"

import { getSubmissionList } from "@/api"
import { LoadingSpinner } from "@/components/shared"
import EmptyIcon from "@/components/shared/icons/empty-icon"
import { ToggleFilter } from "@/components/shared/ui"
import { Image } from "@/components/ui"
import { Button } from "@/components/ui/button"
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel"
import { getCampaignExplore } from "@/features/jobs/explore/api/explore.api"
import { CampaignDetailsSheet } from "@/features/jobs/explore/components/campaign"
import { EFilterStatus, EFolderAssets } from "@/modules/helpers/enum"
import { useInfiniteScrollQuery } from "@/modules/helpers/hooks"
import { useAuthStore, useCurrentUser } from "@/modules/helpers/hooks/shared"
import {
  COMMISSION_RANGE_VALUE_MAP,
  DEFAULT_FILTER_EXPLORE,
  useFilterExplore
} from "@/modules/helpers/hooks/shared/use-filter-explore"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { getImageByNameFromS3, sendHaptic, sendOpenLink } from "@/modules/helpers/utils"
import { openTikTokShowcaseAuth } from "@/modules/helpers/utils"
import type { Campaign } from "@/modules/types"

import { CampaignItem, CampaignItemSkeleton, FilterExplore } from "."

const BannerCarousel = () => {
  const router = useRouter()
  const [api, setApi] = useState<any>()
  const [current, setCurrent] = useState(0)

  const images = [
    {
      src: "/banner/zzp-community.png",
      alt: "zzp-community",
      link: "https://zalo.me/g/wrhjex224",
      type: "external"
    },
    {
      src: "/banner/hot-new.png",
      alt: "hot-new",
      link: "27",
      type: "internal"
    }
  ]

  useEffect(() => {
    if (!api) return

    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  const handleImageClick = (image: (typeof images)[0]) => {
    sendHaptic()
    if (!image.link) return
    if (image.type === "internal") {
      router.navigate({ to: "/blog/$id", params: { id: image.link } })
    } else {
      sendOpenLink(image.link)
    }
  }

  return (
    <Carousel
      setApi={setApi}
      className="flex-1 aspect-square relative rounded-2xl overflow-hidden"
      opts={{
        loop: true
      }}
    >
      <CarouselContent>
        {images.map((image, index) => (
          <CarouselItem key={index}>
            <div
              className="relative w-full h-full cursor-pointer"
              onClick={() => handleImageClick(image)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover select-none"
                draggable={false}
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>

      {/* Indicator dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {images.map((_, index) => (
          <div
            key={index}
            className={cn("w-1.5 h-1.5 rounded-full transition-all duration-300", {
              "bg-white w-4": index === current,
              "bg-white/50": index !== current
            })}
          />
        ))}
      </div>
    </Carousel>
  )
}

const SearchHeader = () => (
  <div className="flex justify-between items-center gap-2 py-1 mb-2">
    <Link
      to="/s/campaign"
      onClick={() => sendHaptic()}
      className="rounded-xl transition-all ease-in-out h-12 z-40 lg:w-[240px] flex-1 gap-2 items-center px-3 flex border border-gray-200 bg-white"
    >
      <SearchNormal1 size={20} color="#0091AA" />
      <p className="text-xs font-light text-[rgba(130,_146,_170,_1)]">Tìm kiếm chiến dịch</p>
    </Link>
    <FilterExplore />
  </div>
)

const Tasks = () => {
  const { user } = useCurrentUser()
  const router = useRouter()
  const { logout } = useAuthStore()
  const queryClient = useQueryClient()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const isCompleteProfile = useMemo(() => {
    return (
      user?.username &&
      user?.phoneNumber &&
      user?.channelOrientation?.length &&
      user?.channelOrientation?.length > 0
    )
  }, [user])

  function handleAuthorize() {
    if (user?.isAuthCreatorTap || !user?.id) return

    openTikTokShowcaseAuth(user.id)

    // Revalidate user info after authorization
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["user_info"] })
    }, 6000)
  }

  const { data: dataCampaigns } = useInfiniteScrollQuery({
    queryFn: getSubmissionList,
    params: {
      status: EFilterStatus.REMINDER
    },
    queryKey: ["submission"]
  })

  const reminders = useMemo(() => dataCampaigns?.list || [], [dataCampaigns?.list])

  const initTask = useMemo(
    () => [
      {
        title: "Hoàn tất hồ sơ",
        description: isCompleteProfile ? "Đã hoàn tất" : "Chưa hoàn tất.",
        icon: <User size={14} color="#fff" />,
        className: isCompleteProfile ? "bg-secondary-800" : "bg-gray-400",
        completed: isCompleteProfile,
        link: isCompleteProfile
          ? undefined
          : {
              to: "/profile",
              search: {
                tab: "profile"
              }
            }
      },
      {
        title: "Ủy quyền trưng bày",
        description: user?.isAuthCreatorTap ? "Đã ủy quyền." : "Ủy quyền ngay.",
        icon: <EmojiHappy size={14} color="#fff" />,
        className: user?.isAuthCreatorTap ? "bg-primary-800" : "bg-gray-400",
        color: user?.isAuthCreatorTap ? "#fff" : "#000",
        completed: user?.isAuthCreatorTap,
        link: user?.isAuthCreatorTap
          ? undefined
          : {
              to: "/authorize-showcase"
            }
      },
      {
        title: "Ủy quyền TikTok",
        description:
          user?.tiktoks?.link && user?.tiktoks?.followerCount ? "Đã ủy quyền." : "Ủy quyền ngay.",
        icon: <MagicStar size={14} color="#fff" />,
        className:
          user?.tiktoks?.link && user?.tiktoks?.followerCount ? "bg-primary" : "bg-gray-400",
        color: user?.tiktoks?.link && user?.tiktoks?.followerCount ? "#fff" : "#000",
        completed: user?.tiktoks?.link && user?.tiktoks?.followerCount,
        link:
          user?.tiktoks?.link && user?.tiktoks?.followerCount
            ? undefined
            : {
                to: "/authorize-tiktok"
              }
      }
    ],
    [isCompleteProfile, user?.isAuthCreatorTap, user?.tiktoks?.link, user?.tiktoks?.followerCount]
  )

  const shouldPushTasksDown = isCompleteProfile && user?.isAuthCreatorTap && reminders.length > 2

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current
    if (container) {
      const isScrollable = container.scrollHeight > container.clientHeight
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 5
      setShowScrollButton(isScrollable && !isAtBottom)
    }
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // Initial check
    checkScrollPosition()

    // Listen to scroll events
    container.addEventListener("scroll", checkScrollPosition, { passive: true })

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(() => {
      checkScrollPosition()
    })
    resizeObserver.observe(container)

    // Check again after a short delay when data changes
    const timeoutId = setTimeout(checkScrollPosition, 200)

    return () => {
      container.removeEventListener("scroll", checkScrollPosition)
      resizeObserver.disconnect()
      clearTimeout(timeoutId)
    }
  }, [reminders, initTask, checkScrollPosition])

  const handleScrollDown = () => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollBy({
        top: container.clientHeight * 0.8,
        behavior: "smooth"
      })
    }
  }

  return (
    <div className="flex-1 flex flex-col bg-background h-full w-full rounded-2xl overflow-hidden aspect-square gap-2">
      <div className="p-3 h-full w-full flex flex-col gap-2 overflow-hidden">
        <p className="text-gradient-primary text-xs font-medium">Bạn có lời nhắc</p>
        <div className="flex flex-col gap-2 relative flex-1 min-h-0">
          <div
            ref={scrollContainerRef}
            className="flex flex-col gap-1 h-full overflow-y-auto scroll-smooth"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {!shouldPushTasksDown &&
              initTask.map((task, index) => (
                <div
                  key={`${task.link} - ${index}`}
                  onClick={() => {
                    if (!task.link) return
                    if (task.link.to === "/authorize-showcase") {
                      handleAuthorize()
                      return
                    }
                    if (task.link.to === "/authorize-tiktok") {
                      logout()
                      return
                    }
                    if (task.link) {
                      router.navigate(task.link as any)
                    }
                  }}
                  className="flex relative items-center gap-2 bg-white rounded-lg p-2 pr-3 pl-1"
                >
                  <div key={task.title} className="flex items-center gap-2">
                    <div
                      className={cn(
                        "rounded-full w-8 h-8 flex items-center justify-center",
                        task.className
                      )}
                    >
                      {task.icon}
                    </div>
                    <div>
                      <p className="text-xs font-medium">{task.title}</p>
                      <p
                        className={cn("text-xs", {
                          "text-secondary-800": task.completed,
                          "text-gray-400": !task.completed
                        })}
                      >
                        {task.description}
                      </p>
                    </div>
                  </div>
                  {!task?.completed && (
                    <div className="absolute right-0.5">
                      <ArrowRight2 size={12} />
                    </div>
                  )}
                </div>
              ))}
            {reminders &&
              reminders.map((reminder) => (
                <div
                  onClick={() => {
                    router.navigate({ to: "/manage", search: { tab: "REMINDER" } })
                  }}
                  className={cn("flex gap-1.5 items-center bg-white rounded-md p-1.5 w-full")}
                >
                  <div
                    className={cn(
                      "rounded-full overflow-hidden border border-primary-100 max-w-8 min-w-8 h-8 flex items-center justify-center"
                    )}
                  >
                    <Image
                      src={getImageByNameFromS3(
                        reminder?.affCampaigns?.thumbnailName,
                        reminder?.affCampaigns?.createdBy,
                        EFolderAssets.THUMBNAIL_AFFILIATE_CAMPAIGN
                      )}
                      alt="icon"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{reminder?.affCampaigns?.title}</p>
                    <p
                      style={{
                        color: reminder?.metadataContent?.color
                      }}
                      className="text-xss font-medium truncate"
                    >
                      {reminder?.metadataContent?.status}
                    </p>
                  </div>
                  {/* <span className="text-xs font-semibold whitespace-nowrap">Trạng thái:</span> */}
                </div>
              ))}
            {shouldPushTasksDown &&
              initTask.map((task, index) => (
                <div
                  key={`${task.link} - ${index}`}
                  onClick={() => {
                    if (!task.link) return
                    if (task.link.to === "authorize-showcase") {
                      handleAuthorize()
                      return
                    }
                    if (task.link.to === "authorize-tiktok") {
                      logout()
                      return
                    }
                    if (task.link) {
                      router.navigate({ to: task.link as any })
                    }
                  }}
                  className="flex relative items-center gap-2 bg-white rounded-lg p-2 pr-3 pl-1"
                >
                  <div key={task.title} className="flex items-center gap-2">
                    <div
                      className={cn(
                        "rounded-full w-8 h-8 flex items-center justify-center",
                        task.className
                      )}
                    >
                      {task.icon}
                    </div>
                    <div>
                      <p className="text-xs font-medium">{task.title}</p>
                      <p
                        className={cn("text-xs", {
                          "text-secondary-800": task.completed,
                          "text-gray-400": !task.completed
                        })}
                      >
                        {task.description}
                      </p>
                    </div>
                  </div>
                  {!task?.completed && (
                    <div className="absolute right-0.5">
                      <ArrowRight2 size={12} />
                    </div>
                  )}
                </div>
              ))}
          </div>
          {showScrollButton && (
            <button
              onClick={handleScrollDown}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white/20 rounded-full p-0.5 shadow-lg border border-gray-200 z-10"
              aria-label="Scroll down"
            >
              <ArrowDown2 size={16} color="black" />
            </button>
          )}
        </div>
      </div>
      {/* Z Xu Balance Card */}
    </div>
  )
}

type CampaignNotFoundProps = {
  onReload: () => void
}

const CampaignNotFound = ({ onReload }: CampaignNotFoundProps) => {
  const { user } = useCurrentUser()
  return (
    <div className="flex flex-col items-center justify-center h-full my-4 px-2">
      <div className="flex flex-col items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <EmptyIcon w={280} h={280} />
          <p className="text-primary-800 text-base font-semibold mt-2">
            Không tìm thấy chiến dịch!
          </p>

          {Number(user?.tiktoks?.followerCount) < 1000 && (
            <>
              <p className="text-sm text-gray-500 font-semibold text-center mt-2">
                Tài khoản TikTok của bạn cần đủ 1.000 follower (hoặc theo yêu cầu riêng của Nhãn
                hàng) để xem các chiến dịch trên ứng dụng ZZP.
              </p>
              <p className="text-sm text-gray-500 font-semibold text-center mt-2">
                {" "}
                Nếu đã đủ, vui lòng{" "}
                <span className="text-primary underline cursor-pointer" onClick={onReload}>
                  tải lại Trang chủ
                </span>{" "}
                để hệ thống tự động cập nhật ngay.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const CampaignsPage = () => {
  const [type, setType] = useQueryState("type", parseAsString.withDefault("ALL"))
  const { filter } = useFilterExplore()
  const filterParams = useMemo(() => {
    if (!filter) return {}
    const params: Record<string, string | boolean | null> = {}

    if (filter.order !== DEFAULT_FILTER_EXPLORE.order) {
      params.order = filter.order
    }

    if (filter.isSelfFaceRequired !== DEFAULT_FILTER_EXPLORE.isSelfFaceRequired) {
      params.isSelfFaceRequired = filter.isSelfFaceRequired
    }

    if (filter.commissionRange !== DEFAULT_FILTER_EXPLORE.commissionRange) {
      const range = COMMISSION_RANGE_VALUE_MAP[filter.commissionRange]
      if (range?.startCommissionRate) {
        params.startCommissionRate = range.startCommissionRate
      }
      if (range?.endCommissionRate) {
        params.endCommissionRate = range.endCommissionRate
      }
    }

    return params
  }, [filter])

  const filterSignature = useMemo(() => JSON.stringify(filterParams), [filterParams])
  const [showScrollTop, setShowScrollTop] = useState(false)
  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, forceRefetch } =
    useInfiniteScrollQuery({
      queryKey: ["explore", type, filterSignature],
      queryFn: getCampaignExplore,
      params: {
        take: 50,
        ...filterParams,
        ...(type === "ALL" ? {} : { haveCast: type === "HAS_CAST_CAMPAIGN" })
      }
    })

  useEffect(() => {
    // Listen to the parent scrollable container from app layout
    const scrollableElement = document.querySelector("#scrollable") as HTMLElement
    if (!scrollableElement) return

    const handleScroll = () => {
      const scrollTop = scrollableElement.scrollTop
      setShowScrollTop(scrollTop > 300)
    }

    scrollableElement.addEventListener("scroll", handleScroll)
    return () => {
      scrollableElement.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const handleScrollToTop = () => {
    sendHaptic()
    const scrollableElement = document.querySelector("#scrollable") as HTMLElement
    scrollableElement?.scrollTo({
      top: 0,
      behavior: "smooth"
    })
  }

  const handleRefresh = async () => {
    await forceRefetch()
  }

  return (
    <div className="flex flex-col relative w-full">
      <div className="z-40 gap-2 flex flex-col sticky top-0 px-2">
        <SearchHeader />
      </div>
      <div className="w-full mb-4 flex gap-2 p-2 bg-gradient-primary">
        {/* Left Section: Banner Carousel */}
        <BannerCarousel />

        {/* Right Section: Z Xu Balance and Tasks */}
        <Tasks />
      </div>
      <div className="px-2">
        <ToggleFilter
          options={[
            {
              value: "ALL",
              label: "Tất cả",
              icon: <Star />
            },
            {
              value: "NO_CAST_CAMPAIGN",
              label: "Không Cast",
              icon: <Gift />
            },
            {
              value: "HAS_CAST_CAMPAIGN",
              label: "Có Cast",
              icon: <PiFire />
            }
          ]}
          value={type}
          onChange={setType}
          defaultValue="NO_CAST_CAMPAIGN"
          isToggle
        />
      </div>
      <PullToRefresh
        onRefresh={handleRefresh}
        pullingContent={""}
        maxPullDownDistance={80}
        refreshingContent={<LoadingSpinner className="w-20 h-20" />}
      >
        <div className="flex-1 flex flex-col px-2" id="scrollable">
          {isLoading && (
            <div className="grid-cols-2 pb-10 gap-1 grid">
              <CampaignItemSkeleton />
              <CampaignItemSkeleton />
              <CampaignItemSkeleton />
              <CampaignItemSkeleton />
            </div>
          )}
          {!isLoading && !data?.list?.length && <CampaignNotFound onReload={handleRefresh} />}
          <InfiniteScroll
            dataLength={data?.list?.length || 0}
            next={() => !isFetchingNextPage && hasNextPage && fetchNextPage()}
            hasMore={hasNextPage}
            scrollableTarget="scrollable"
            loader={<LoadingSpinner className="w-20 h-20" />}
          >
            <div className="grid grid-cols-2 gap-2 pb-4">
              {data?.list?.map((item: Campaign) => (
                <CampaignItem key={item?.id} data={item} />
              ))}
            </div>
          </InfiniteScroll>
        </div>
      </PullToRefresh>
      {showScrollTop && (
        <Button
          onClick={handleScrollToTop}
          size="icon"
          className="fixed bottom-30 right-4 z-50 rounded-full w-8 h-8 bg-primary-50 border-primary border shadow-lg transition-all duration-300"
          aria-label="Scroll to top"
        >
          <ArrowUp2 size={20} color="#0091aa" />
        </Button>
      )}
      <CampaignDetailsSheet />
    </div>
  )
}

export default CampaignsPage
